/**
 * Modal pro úpravu akce — vyvolaný dvojklikem na datum v Přehledu.
 * Drží se klíčových polí (datum, časy, místo, status, poznámka) — finanční
 * rozpis se nadále edituje v Administraci.
 *
 * openEventEditModal(eventId, onSaved)
 */
import { supabase } from '../lib/supabase.js';
import { calcLeadTime, calcDepartureTime } from '../utils/planovac.js';

function esc(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function timeInput(name, value, label) {
    return `
        <label class="se-field" style="flex:1;">
            <span>${label}</span>
            <input type="time" name="${name}" value="${esc(value || '')}">
        </label>`;
}

export async function openEventEditModal(eventId, onSaved) {
    if (!eventId) return;

    // Načti akci + místa (s dopravními daty) + profily + nastavení
    const [evRes, placesRes, profilesRes, settingsRes] = await Promise.all([
        supabase.from('events').select('*').eq('id', eventId).single(),
        supabase.from('places').select('id, name, distance_pilsen, distance_stribro, travel_time_pilsen, travel_time_stribro, extra_reserve_min').order('name'),
        supabase.from('profiles').select('id, name').order('name'),
        supabase.from('settings').select('key, value')
    ]);
    if (evRes.error) { alert('Načtení selhalo: ' + evRes.error.message); return; }
    const ev       = evRes.data;
    const places   = placesRes.data || [];
    const profiles = profilesRes.data || [];
    const settings = Object.fromEntries((settingsRes.data || []).map(s => [s.key, s.value]));

    const m = document.createElement('div');
    m.className = 'modal-overlay open';
    m.id = 'event-edit-modal';

    const isBlock = ev.type === 'block';

    const gigForm = `
        <label class="se-field">
            <span>Datum</span>
            <input type="date" name="event_date" value="${esc(ev.event_date)}" required>
        </label>
        <div style="display:flex; gap:8px; margin-top:10px;">
            ${timeInput('time_start', ev.time_start, 'Začátek')}
            ${timeInput('time_end',   ev.time_end,   'Konec')}
        </div>
        <label class="se-field" style="margin-top:10px;">
            <span>Místo</span>
            <select name="place_id">
                <option value="">— vyber —</option>
                ${places.map(p => `<option value="${esc(p.id)}" ${ev.place_id === p.id ? 'selected' : ''}>${esc(p.name)}</option>`).join('')}
            </select>
        </label>
        <div style="display:flex; gap:8px; margin-top:10px;">
            ${timeInput('meeting_time',      ev.meeting_time,      'Sraz na místě')}
            ${timeInput('departure_pilsen',  ev.departure_pilsen,  'Odjezd z Plzně')}
            ${timeInput('departure_stribro', ev.departure_stribro, 'Odjezd ze Stříbra')}
        </div>
        <p class="hint" style="margin-top:4px; font-size:11px; color:var(--text-muted);">
            ↑ Automaticky se přepočtou při změně začátku nebo místa. Lze upravit ručně.
        </p>
        <label class="se-field" style="margin-top:10px;">
            <span>Extra rezerva <span style="font-weight:400; font-size:11px; color:var(--text-muted);">ples = víc předem, min</span></span>
            <input type="number" name="extra_reserve_min" value="${ev.extra_reserve_min || 0}" min="0" step="15" style="width:80px;">
        </label>
        <label class="se-field" style="margin-top:10px;">
            <span>Stav akce</span>
            <select name="status" required>
                <option value="pending"   ${ev.status === 'pending'   ? 'selected' : ''}>V jednání (neprosakuje na veřejný web)</option>
                <option value="confirmed" ${ev.status !== 'pending'   ? 'selected' : ''}>Potvrzeno (jde na web)</option>
            </select>
        </label>
        <label class="se-field" style="margin-top:10px;">
            <span>Pořadatel</span>
            <input type="text" name="organizer" value="${esc(ev.organizer || '')}">
        </label>
        <label class="se-field" style="margin-top:10px;">
            <span>Poznámka</span>
            <textarea name="notes" rows="2">${esc(ev.notes || '')}</textarea>
        </label>
    `;

    const blockForm = `
        <label class="se-field">
            <span>Datum začátku</span>
            <input type="date" name="event_date" value="${esc(ev.event_date)}" required>
        </label>
        <label class="se-field" style="margin-top:10px;">
            <span>Datum konce (jen pokud delší blokace)</span>
            <input type="date" name="event_date_end" value="${esc(ev.event_date_end || '')}">
        </label>
        <label class="se-field" style="margin-top:10px;">
            <span>Blokuje</span>
            <select name="blocked_for">
                <option value="">— vyber —</option>
                ${profiles.map(p => `<option value="${esc(p.id)}" ${ev.blocked_for === p.id ? 'selected' : ''}>${esc(p.name)}</option>`).join('')}
            </select>
        </label>
        <label class="se-field" style="margin-top:10px;">
            <span>Důvod</span>
            <input type="text" name="block_reason" value="${esc(ev.block_reason || '')}">
        </label>
    `;

    m.innerHTML = `
        <div class="modal-window" style="max-width:540px;">
            <div class="modal-head">
                <h2>${isBlock ? 'Upravit blokaci' : 'Upravit akci'}</h2>
                <button class="modal-close" type="button" id="ee-close">×</button>
            </div>
            <form id="ee-form">
                <div class="modal-body">
                    ${isBlock ? blockForm : gigForm}
                    <p id="ee-error" style="color:var(--accent); font-size:13px; margin-top:10px; display:none;"></p>
                </div>
                <div class="modal-foot">
                    <button class="btn rep-btn-danger" type="button" id="ee-delete" style="margin-right:auto;">🗑 Smazat</button>
                    <button class="btn" type="button" id="ee-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="submit">Uložit</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(m);

    // Auto-přepočet časů při změně začátku nebo místa (jen pro gig)
    if (!isBlock) {
        const startInput   = m.querySelector('[name="time_start"]');
        const placeSelect  = m.querySelector('[name="place_id"]');
        const extraInput   = m.querySelector('[name="extra_reserve_min"]');

        function recalcTimes() {
            const start   = startInput?.value;
            const placeId = placeSelect?.value;
            if (!start || !placeId) return;
            const place = places.find(p => p.id === placeId);
            if (!place) return;

            const extraMin = parseInt(extraInput?.value, 10) || 0;
            const lead = calcLeadTime(place, settings, extraMin);
            const setup = (Number(settings.time_reserve_start) || 20)
                        + (Number(settings.time_stage_setup)   || 40)
                        + (Number(settings.time_moving)        || 10)
                        + (Number(place.extra_reserve_min)     || 0)
                        + extraMin;

            const apply = (name, val) => {
                const el = m.querySelector(`[name="${name}"]`);
                if (el && val) el.value = val;
            };
            apply('meeting_time',      calcDepartureTime(start, setup));
            apply('departure_pilsen',  calcDepartureTime(start, lead.pilsenMin));
            apply('departure_stribro', calcDepartureTime(start, lead.stribroMin));
        }

        startInput?.addEventListener('input', recalcTimes);
        placeSelect?.addEventListener('change', recalcTimes);
        extraInput?.addEventListener('input', recalcTimes);
    }

    const close = () => m.remove();
    m.querySelector('#ee-close').onclick = close;
    m.querySelector('#ee-cancel').onclick = close;
    m.onclick = (e) => { if (e.target === m) close(); };
    document.addEventListener('keydown', function onEsc(e) {
        if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
    });

    m.querySelector('#ee-delete').onclick = async () => {
        if (!confirm('Opravdu smazat tuto akci? Tato akce je nevratná.')) return;
        const { error } = await supabase.from('events').delete().eq('id', eventId);
        if (error) { alert('Smazání selhalo: ' + error.message); return; }
        close();
        onSaved?.();
    };

    m.querySelector('#ee-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const errEl = m.querySelector('#ee-error');
        errEl.style.display = 'none';

        const fd = new FormData(e.target);
        const fields = Object.fromEntries(fd.entries());

        const update = isBlock
            ? {
                event_date:     fields.event_date,
                event_date_end: fields.event_date_end || null,
                blocked_for:    fields.blocked_for   || null,
                block_reason:   fields.block_reason  || null
              }
            : {
                event_date:        fields.event_date,
                time_start:        fields.time_start        || null,
                time_end:          fields.time_end          || null,
                place_id:          fields.place_id          || null,
                meeting_time:      fields.meeting_time      || null,
                departure_pilsen:  fields.departure_pilsen  || null,
                departure_stribro: fields.departure_stribro  || null,
                extra_reserve_min: parseInt(fields.extra_reserve_min, 10) || 0,
                status:            fields.status,
                organizer:         fields.organizer          || null,
                notes:             fields.notes              || null
              };

        const { error } = await supabase.from('events').update(update).eq('id', eventId);
        if (error) {
            errEl.textContent = 'Uložení selhalo: ' + error.message;
            errEl.style.display = 'block';
            return;
        }
        close();
        onSaved?.();
    });
}
